import { ImgID } from "../../image_loader/img_id.js";
import { ColorScheme } from "../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../common_ui_elements/text_style_scheme.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../common_ui_elements/ui_text_element.js";
import { AbstractDialog } from "./abstract_dialog.js";
import { DialogManager } from "./dialog_manager.js";
import { ScreenManager, Screens } from "../../screen_manager/screen_manager.js";
import { StatisticsStorage } from "../../storage/statistics_storage.js";
import { Vector2 } from "../../common/vector2.js";
import { Game } from "../../game.js";
import { BuyBoosterDialog } from "./buy_booster_dialog.js";
import { BuyBoosterType } from "./buy_booster_types.js";
import { CumulativeBoosterID } from "../../cumulative_boosters/cumulative_booster_id.js";
import { CumulativeBoosterService } from "../../cumulative_boosters/cumulative_booster_service.js";
import { UIElementHolder } from "../common_ui_elements/ui_element_holder.js";
import { LocalizationManager } from "../../localization/localization_manager.js";
import { DAILY_CHALLENGE_DAYS_COUNT } from "../../daily_challenge/daily_challenge_config.js";
import { DailyChallengeService } from "../../daily_challenge/daily_challenge_service.js";
import { PlayDialogState } from "./play_dialog.js";

interface PassiveBoosterSlotView
{
  background: UIImage;
  badge: UIImage;
  badge_size: number;
  count_text: UITextElement | null;
}

export class DailyChallengePlayDialog extends AbstractDialog
{
  private static readonly PASSIVE_BOOSTER_ID = CumulativeBoosterID.Mine;
  private static readonly PASSIVE_BOOSTER_ICON = ImgID.UI_PassiveBoosterBomb;
  private static readonly DESIGN_W = 961.26;
  private static readonly DESIGN_H = 1335;
  private static readonly BG_W = 924;
  private static readonly BG_H = 1140;
  private static readonly BG_Y = 309;
  private readonly stage: number;
  private readonly state: PlayDialogState;
  private readonly on_close?: () => void;
  private readonly on_retry?: () => void | Promise<void>;
  private is_starting = false;
  private start_inflight = false;
  private close_btn!: UIImage;
  private booster_tooltip: UIElementHolder | null = null;
  private booster_tooltip_dismiss_layer: UIElementHolder | null = null;
  private passive_booster_selected = false;
  private passive_slot: PassiveBoosterSlotView | null = null;

  constructor(
    stage: number,
    state: PlayDialogState = PlayDialogState.Play,
    on_close?: () => void,
    on_retry?: () => void | Promise<void>
  )
  {
    const body_w = LayoutScaler.widthFromDesign( DailyChallengePlayDialog.DESIGN_W );
    const body_h = LayoutScaler.heightFromDesign( DailyChallengePlayDialog.DESIGN_H );

    const body_x = ( DialogManager.getWidth() - body_w ) / 2;
    const body_y = ( DialogManager.getHeight() - body_h ) / 2
      - LayoutScaler.heightFromDesign( 140 );

    super( DialogManager.ctx, body_x, body_y, body_w, body_h );

    this.stage = stage;
    this.state = state;
    this.on_close = on_close;
    this.on_retry = on_retry;

    this.createBackground();
    this.createTopImage();
    this.createTopBanner();
    this.createInfoFrame();
    this.createPlayButton();
    this.createCloseButton();
  }

  public static open(
    stage: number,
    state: PlayDialogState = PlayDialogState.Play,
    on_close?: () => void,
    on_retry?: () => void | Promise<void>
  ): DailyChallengePlayDialog
  {
    const dialog = new DailyChallengePlayDialog( stage, state, on_close, on_retry );
    DialogManager.openDialog( dialog );
    return dialog;
  }

  private createBackground(): void
  {
    const design_bg_x = ( DailyChallengePlayDialog.DESIGN_W - DailyChallengePlayDialog.BG_W ) / 2;
    const design_seam_overlap = 4;
    const design_half_w = DailyChallengePlayDialog.BG_W / 2 + design_seam_overlap;
    const design_right_x = design_bg_x + DailyChallengePlayDialog.BG_W - design_half_w;

    const half_w = LayoutScaler.widthFromDesign( design_half_w );
    const half_h = LayoutScaler.heightFromDesign( DailyChallengePlayDialog.BG_H );
    const left_x = LayoutScaler.xFromDesign( design_bg_x );
    const right_x = LayoutScaler.xFromDesign( design_right_x );
    const bg_y = LayoutScaler.yFromDesign( DailyChallengePlayDialog.BG_Y );

    const left_half = new UIImage( this.ctx, left_x, bg_y, half_w, half_h, ImgID.UI_PlayDialogBgHalfSuperHard );
    this.addChild( left_half );

    const right_half = new UIImage( this.ctx, right_x, bg_y, half_w, half_h, ImgID.UI_PlayDialogBgHalfSuperHard );
    right_half.setFlipX( true );
    this.addChild( right_half );
  }

  private createTopImage(): void
  {
    const design_img_w = 641;
    const design_img_h = 247;
    const design_img_x = ( DailyChallengePlayDialog.DESIGN_W - design_img_w ) / 2;

    const img_x = LayoutScaler.xFromDesign( design_img_x );
    const img_y = LayoutScaler.yFromDesign( 0 );
    const img_w = LayoutScaler.widthFromDesign( design_img_w );
    const img_h = LayoutScaler.heightFromDesign( design_img_h );

    const img = new UIImage( this.ctx, img_x, img_y, img_w, img_h, ImgID.UI_PlayDialogTop );
    this.addChild( img );
  }

  private createTopBanner(): void
  {
    const design_banner_x = -5;
    const design_banner_y = 104;
    const design_banner_w = 979;
    const design_banner_h = 336;
    const design_level_w = 457;
    const design_level_h = 86;
    const design_level_overlap = 50;

    const banner_x = LayoutScaler.xFromDesign( design_banner_x );
    const banner_y = LayoutScaler.yFromDesign( design_banner_y );
    const banner_w = LayoutScaler.widthFromDesign( design_banner_w );
    const banner_h = LayoutScaler.heightFromDesign( design_banner_h );

    const banner = new UIImage( this.ctx, banner_x, banner_y, banner_w, banner_h, ImgID.UI_TryAgainChallengeTopImage );
    this.addChild( banner );

    const level_w = LayoutScaler.widthFromDesign( design_level_w );
    const level_h = LayoutScaler.heightFromDesign( design_level_h );
    const level_x = ( banner_w - level_w ) / 2;
    const level_y = banner_h - LayoutScaler.heightFromDesign( design_level_overlap );

    const level_bg = new UIImage( this.ctx, level_x, level_y, level_w, level_h, ImgID.UI_TryAgainChallengeLevelBg );
    banner.addChild( level_bg );

    const text_x = LayoutScaler.widthFromDesign( 30 );
    const text_w = level_w - text_x * 2;

    const text = new UITextElement( this.ctx, text_x, 0, text_w, level_h, {
      localization_key: "PLAY_DIALOG.TITLE",
      text_variable_params: [ `${this.stage}/${DAILY_CHALLENGE_DAYS_COUNT}` ],
      font_size: LayoutScaler.textSizeFromDesign( 50 ),
      font_weight: 900,
      text_color: ColorScheme.white,
      text_style: TextStyleScheme.geologica,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
      outline_color: ColorScheme.daily_challenge_level_outline,
      outline_offset_y: LayoutScaler.yFromDesign( 2 ),
      scale_to_width: true,
      auto_wrap: false,
      ...TextStyleScheme.buttonShadow(),
    } );
    level_bg.addChild( text );
  }

  private createInfoFrame(): void
  {
    const design_frame_w = 730;
    const design_frame_h = 444;
    const design_frame_x = ( DailyChallengePlayDialog.DESIGN_W - design_frame_w ) / 2;
    const design_frame_y = 580;

    const frame_x = LayoutScaler.xFromDesign( design_frame_x );
    const frame_y = LayoutScaler.yFromDesign( design_frame_y );
    const frame_w = LayoutScaler.widthFromDesign( design_frame_w );
    const frame_h = LayoutScaler.heightFromDesign( design_frame_h );

    const frame = new UIImage( this.ctx, frame_x, frame_y, frame_w, frame_h, ImgID.UI_GameStartContentBg );
    this.addChild( frame );

    const design_title_w = 560;
    const design_title_h = 70;
    const design_title_x = ( design_frame_w - design_title_w ) / 2;

    const title_x = LayoutScaler.xFromDesign( design_title_x );
    const title_y = LayoutScaler.yFromDesign( 30 );
    const title_w = LayoutScaler.widthFromDesign( design_title_w );
    const title_h = LayoutScaler.heightFromDesign( design_title_h );

    const title = new UITextElement( this.ctx, title_x, title_y, title_w, title_h, {
      localization_key: "PLAY_DIALOG.TEXT",
      font_size: LayoutScaler.textSizeFromDesign( 51 ),
      font_weight: 900,
      text_color: ColorScheme.white,
      text_style: TextStyleScheme.geologica,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
      outline_color: ColorScheme.play_dialog_text_outline,
      outline_offset_y: LayoutScaler.yFromDesign( 2 ),
      scale_to_width: true,
      auto_wrap: false,
    } );
    frame.addChild( title );

    const slot_size = 190;
    const slot_x = ( design_frame_w - slot_size ) / 2;
    const slot_y = 140;
    this.createPassiveBoosterSlot( frame, slot_x, slot_y, slot_size );
  }

  private createPassiveBoosterSlot( frame: UIImage, x: number, y: number, size: number ): void
  {
    const booster_id = DailyChallengePlayDialog.PASSIVE_BOOSTER_ID;
    const unlock_level = CumulativeBoosterService.getUnlockLevel( booster_id );
    const is_unlocked = CumulativeBoosterService.isUnlocked( booster_id, StatisticsStorage.load().level );

    const slot_x = LayoutScaler.xFromDesign( x );
    const slot_y = LayoutScaler.yFromDesign( y );
    const slot_w = LayoutScaler.widthFromDesign( size );
    const slot_h = LayoutScaler.heightFromDesign( size );

    const slot = new UIImage( this.ctx, slot_x, slot_y, slot_w, slot_h, ImgID.UI_PassiveBoosterDisabled, undefined, true );
    frame.addChild( slot );

    if ( !is_unlocked )
    {
      const locked_y = LayoutScaler.yFromDesign( -22 );
      const locked_icon = new UIImage( this.ctx, 0, locked_y, slot_w, slot_h, ImgID.UI_GameStartLockedBooster );
      slot.addChild( locked_icon, true );
      this.createLockedLevelLabel( slot, size, unlock_level );
      slot.setOnClick( () => this.showBoosterTooltip(
        slot,
        `${LocalizationManager.t( "BOOSTER.AVAILABLE" )} ${unlock_level}`
      ) );
      return;
    }

    const design_icon_size = 190;
    const icon_x = LayoutScaler.xFromDesign( ( size - design_icon_size ) / 2 );
    const icon_y = LayoutScaler.yFromDesign( -12 );
    const icon_w = LayoutScaler.widthFromDesign( design_icon_size );
    const icon_h = LayoutScaler.heightFromDesign( design_icon_size );

    const icon = new UIImage( this.ctx, icon_x, icon_y, icon_w, icon_h, DailyChallengePlayDialog.PASSIVE_BOOSTER_ICON );
    slot.addChild( icon, true );

    const design_badge_size = 80;
    const badge_x = LayoutScaler.xFromDesign( ( size - design_badge_size ) / 2 );
    const badge_y = LayoutScaler.yFromDesign( 145 );
    const badge_size = LayoutScaler.widthFromDesign( design_badge_size );
    const badge_h = LayoutScaler.heightFromDesign( design_badge_size );

    const badge = new UIImage( this.ctx, badge_x, badge_y, badge_size, badge_h, ImgID.UI_PassiveBoosterCountBadge, undefined, true );
    slot.addChild( badge, true );

    this.passive_slot = { background: slot, badge, badge_size, count_text: null };
    this.refreshPassiveBooster();

    slot.setOnClick( () =>
    {
      if ( CumulativeBoosterService.inventory.getCount( booster_id ) <= 0 )
      {
        void this.openBuyBoosterDialog( booster_id );
        return;
      }
      this.passive_booster_selected = !this.passive_booster_selected;
      this.refreshPassiveBooster();
    } );
  }

  private refreshPassiveBooster(): void
  {
    const view = this.passive_slot;
    if ( !view )
      return;

    const count = CumulativeBoosterService.inventory.getCount( DailyChallengePlayDialog.PASSIVE_BOOSTER_ID );
    const selected = this.passive_booster_selected && count > 0;

    view.background.setImageId( selected ? ImgID.UI_PassiveBoosterEnabled : ImgID.UI_PassiveBoosterDisabled );
    view.badge.setImageId(
      selected
        ? ImgID.UI_PassiveBoosterSelectedBadge
        : count > 0
          ? ImgID.UI_PassiveBoosterCountBadge
          : ImgID.UI_PassiveBoosterPlusBadge
    );

    if ( view.count_text )
    {
      view.badge.removeChild( view.count_text );
      view.count_text = null;
    }
    if ( count > 0 && !selected )
      view.count_text = this.createBoosterCountText( view.badge, view.badge_size, count );

    this.invalidateSubtree();
  }

  private selectedPassiveBoosterId(): CumulativeBoosterID | undefined
  {
    const booster_id = DailyChallengePlayDialog.PASSIVE_BOOSTER_ID;
    if ( !this.passive_booster_selected || CumulativeBoosterService.inventory.getCount( booster_id ) <= 0 )
      return undefined;
    return booster_id;
  }

  private async openBuyBoosterDialog( booster_id: CumulativeBoosterID ): Promise<void>
  {
    const offer = await CumulativeBoosterService.getOrLoadFirstPriceOffer( booster_id );
    if ( offer === null )
    {
      console.warn( `[Boosters] No price offer for booster ${booster_id}` );
      return;
    }

    BuyBoosterDialog.open( {
      booster_type: BuyBoosterType.Firework,
      booster_id,
      offer,
      source: "game_start",
      on_buy: () => this.refreshPassiveBooster(),
      on_reward: () => this.refreshPassiveBooster(),
    } );
  }

  private createLockedLevelLabel( slot: UIImage, design_slot_size: number, unlock_level: number ): void
  {
    const label_y = LayoutScaler.yFromDesign( design_slot_size - 28 );
    const label_w = LayoutScaler.widthFromDesign( design_slot_size );
    const label_h = LayoutScaler.heightFromDesign( 50 );

    const label = new UITextElement( this.ctx, 0, label_y, label_w, label_h, {
      text: `Lv.${unlock_level}`,
      font_size: LayoutScaler.textSizeFromDesign( 38 ),
      font_weight: 900,
      text_color: ColorScheme.white,
      text_style: TextStyleScheme.geologica,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
      outline_color: ColorScheme.continue_overlay_caption_outline,
    } );
    slot.addChild( label, true );
  }

  private showBoosterTooltip( slot: UIImage, tooltip_text: string ): void
  {
    this.hideBoosterTooltip();

    const tape_w = LayoutScaler.widthFromDesign( 675 );
    const tape_h = LayoutScaler.heightFromDesign( 200 );
    const bottom_w = LayoutScaler.widthFromDesign( 110.46 );
    const bottom_h = LayoutScaler.heightFromDesign( 77.12 );
    const side_margin = LayoutScaler.widthFromDesign( 33 );
    const dialog_rect = this.getAbsoluteRect();
    const slot_rect = slot.getAbsoluteRect();
    const slot_center_x = slot_rect.x - dialog_rect.x + slot_rect.width / 2;

    const tape_x = Math.max(
      side_margin,
      Math.min( slot_center_x - tape_w / 2, this.rect.getW() - side_margin - tape_w )
    );
    const tape_y = slot_rect.y - dialog_rect.y - LayoutScaler.heightFromDesign( 219.75 );
    const bottom_x = Math.max(
      0,
      Math.min( slot_center_x - tape_x - bottom_w / 2, tape_w - bottom_w )
    );
    const bottom_y = LayoutScaler.heightFromDesign( 167.68 );
    const tooltip_h = Math.max( tape_h, bottom_y + bottom_h );

    const dismiss_layer = new UIElementHolder( this.ctx, 0, 0, this.rect.getW(), this.rect.getH() );
    dismiss_layer.setOnClick( () => this.hideBoosterTooltip(), true );

    const tooltip = new UIElementHolder( this.ctx, tape_x, tape_y, tape_w, tooltip_h );
    tooltip.setClickable( false );

    const tape = new UINineSliceElement( this.ctx, 0, 0, tape_w, tape_h, ImgID.UI_GameBoosterLockedTape, "booster_tape" );
    tooltip.addChild( tape );

    const text_x = LayoutScaler.xFromDesign( 30 );
    const text_y = LayoutScaler.yFromDesign( 20 );
    const text_w = tape_w - LayoutScaler.widthFromDesign( 60 );
    const text_h = tape_h - LayoutScaler.heightFromDesign( 40 );

    const text = new UITextElement( this.ctx, text_x, text_y, text_w, text_h, {
      text: tooltip_text,
      font_size: LayoutScaler.textSizeFromDesign( 52 ),
      font_weight: 900,
      text_color: ColorScheme.white,
      text_style: TextStyleScheme.geologica,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
      outline_color: ColorScheme.continue_overlay_caption_outline,
      fit_font_size: true,
      min_font_size: LayoutScaler.textSizeFromDesign( 30 ),
      line_height: 0.95,
      auto_wrap: true,
    } );
    tape.addChild( text );

    const bottom = new UIImage( this.ctx, bottom_x, bottom_y, bottom_w, bottom_h, ImgID.UI_GameBoosterBottom );
    tooltip.addChild( bottom );

    this.booster_tooltip_dismiss_layer = dismiss_layer;
    this.addChild( dismiss_layer );
    this.booster_tooltip = tooltip;
    this.addChild( tooltip );
    this.invalidateSubtree();
  }

  private hideBoosterTooltip(): void
  {
    if ( this.booster_tooltip )
      this.removeChild( this.booster_tooltip );
    if ( this.booster_tooltip_dismiss_layer )
      this.removeChild( this.booster_tooltip_dismiss_layer );

    this.booster_tooltip = null;
    this.booster_tooltip_dismiss_layer = null;
    this.invalidateSubtree();
  }

  private createBoosterCountText( badge: UIImage, badge_size: number, count: number ): UITextElement
  {
    const count_text = new UITextElement( this.ctx, 0, 0, badge_size, badge_size, {
      text: count.toString(),
      font_size: LayoutScaler.textSizeFromDesign( 40 ),
      font_weight: 900,
      text_color: ColorScheme.white,
      text_style: TextStyleScheme.geologica,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      shadow_blur: LayoutScaler.widthFromDesign( 6 ),
      shadow_color: ColorScheme.black_transparent,
      shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
      scale_to_width: true,
      auto_wrap: false,
    } );
    badge.addChild( count_text, true );
    return count_text;
  }

  private createPlayButton(): void
  {
    const design_btn_w = 650;
    const design_btn_h = 285;
    const design_btn_y = 1100;
    const design_btn_x = ( DailyChallengePlayDialog.DESIGN_W - design_btn_w ) / 2;
    const design_play_font_size = 74;
    const design_try_again_font_size = 64;
    const text_width_ratio = 0.7;

    const btn_x = LayoutScaler.xFromDesign( design_btn_x );
    const btn_y = LayoutScaler.yFromDesign( design_btn_y );
    const btn_w = LayoutScaler.widthFromDesign( design_btn_w );
    const btn_h = LayoutScaler.heightFromDesign( design_btn_h );

    const btn = new UIImage( this.ctx, btn_x, btn_y, btn_w, btn_h, ImgID.UI_GreenFrameBtn, undefined, true );

    const is_try_again = this.state === PlayDialogState.TryAgain;
    const font_size = LayoutScaler.textSizeFromDesign( is_try_again ? design_try_again_font_size : design_play_font_size );
    const text_w = btn_w * text_width_ratio;
    const text_x = ( btn_w - text_w ) / 2;

    const text = new UITextElement( this.ctx, text_x, 0, text_w, btn_h, {
      localization_key: is_try_again ? "TRY_AGAIN" : "PLAY",
      font_size,
      font_weight: 900,
      text_color: ColorScheme.white,
      text_style: TextStyleScheme.geologica,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      auto_wrap: is_try_again,
      scale_to_width: !is_try_again,
      line_height: is_try_again ? 0.95 : 1.1,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
      outline_color: ColorScheme.play_dialog_button_outline,
      ...TextStyleScheme.buttonShadow(),
    } );
    btn.addChild( text, true );

    btn.setOnClick( () => void this.startStage() );
    this.addChild( btn );
  }

  private async startStage(): Promise<void>
  {
    if ( this.start_inflight )
      return;

    this.start_inflight = true;
    this.is_starting = true;

    const level_start_booster_id = this.selectedPassiveBoosterId();
    Game.instance.showLoadingGameScreen();
    DialogManager.closeAllDialogs();

    try
    {
      await Game.instance.waitForLoadingGameScreenPaint();
      await this.on_retry?.();

      const loaded = await DailyChallengeService.loadLevel( this.stage );
      if ( !loaded )
      {
        await ScreenManager.showScreen( Screens.DAILY_CHALLENGE );
        return;
      }

      await ScreenManager.showScreen( Screens.GAME, {
        level: this.stage,
        is_challenge: true,
        level_start_booster_id,
      } );
    }
    finally
    {
      await Game.instance.waitForMinimumLoadingGameScreenDuration();
      Game.instance.hideLoadingGameScreen();
    }
  }

  private createCloseButton(): void
  {
    const design_btn_size = 114;
    const design_btn_margin = 15;
    const design_btn_y = 191;
    const design_btn_x = DailyChallengePlayDialog.DESIGN_W - design_btn_size - design_btn_margin;

    const btn_x = LayoutScaler.xFromDesign( design_btn_x );
    const btn_y = LayoutScaler.yFromDesign( design_btn_y );
    const btn_w = LayoutScaler.widthFromDesign( design_btn_size );
    const btn_h = LayoutScaler.heightFromDesign( design_btn_size );

    const btn = new UIImage( this.ctx, btn_x, btn_y, btn_w, btn_h, ImgID.UI_SettingsDialogCloseBtn, undefined, true );
    btn.setOnClick( () => this.close() );
    this.addChild( btn );
    this.close_btn = btn;
  }

  protected override playAppearAnimation(
    from_scale      = 0.7,
    overshoot_scale = 1.1,
    final_scale     = 1.0,
    scale_up_duration = 136,
    settle_duration   = 96
  ): void
  {
    this.close_btn.setScale( new Vector2( 0, 0 ) );

    super.playAppearAnimation( from_scale, overshoot_scale, final_scale, scale_up_duration, settle_duration );

    this.popCloseButtonAfterAppear( this.close_btn, scale_up_duration + settle_duration );
  }

  public override onDialogWillClose(): void
  {
    if ( this.is_starting )
      return;

    this.on_close?.();
  }

  public name(): string
  {
    return "DailyChallengePlayDialog";
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();
    this.finishRender();
  }
}
